import { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { getBytes, getDownloadURL, ref } from 'firebase/storage';
import { Download, FileText, X } from 'lucide-react';
import { storage } from '../services/firebase';
import type { BookingDocument } from '../types';
import { CardTintStreaks } from './UI';
import { BOOKING_DOC_MAX_BYTES, bookingDocumentEntries } from '../utils/bookingDocuments';
import { emailPreviewHtml, originalEmailPreview } from '../utils/emailPreview';

function BookingEmailViewer({ document: email, download, onClose }: {
  document: BookingDocument; download: BookingDocument; onClose: () => void;
}) {
  const dialog = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const [html, setHtml] = useState('');
  const [error, setError] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState('');
  const [downloadError, setDownloadError] = useState(false);
  useEffect(() => {
    const element = dialog.current!;
    element.showModal();
    return () => element.close();
  }, []);
  useEffect(() => {
    let live = true;
    let objectUrl = '';
    const content = getBytes(ref(storage, email.path), BOOKING_DOC_MAX_BYTES);
    const original = email.path === download.path ? content : getBytes(ref(storage, download.path), BOOKING_DOC_MAX_BYTES);
    content.then(async bytes => {
      const text = new TextDecoder().decode(bytes);
      const preview = email.contentType === 'message/rfc822' ? await originalEmailPreview(bytes)
        : emailPreviewHtml(email.kind === 'email-html' ? text
          : `<pre>${text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')}</pre>`);
      if (live) setHtml(preview);
    }).catch(() => { if (live) setError(true); });
    original.then(bytes => {
      if (!live) return;
      objectUrl = URL.createObjectURL(new Blob([bytes], { type: download.contentType }));
      setDownloadUrl(objectUrl);
    }).catch(() => { if (live) setDownloadError(true); });
    return () => { live = false; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [email.path, download.path, email.contentType, email.kind, download.contentType]);
  const control = 'flex items-center justify-center w-11 h-11 rounded-full bg-app-text/5 hover:bg-app-text/10 text-app-text focus-visible:outline-2 focus-visible:outline-gold';
  return createPortal(
    <dialog ref={dialog} aria-labelledby={titleId} onCancel={event => { event.preventDefault(); onClose(); }}
      className="fixed inset-0 m-0 w-screen max-w-none h-[100dvh] max-h-none p-0 border-0 bg-app-bg text-app-text overflow-hidden [contain:paint]">
      <CardTintStreaks />
      <div className="relative z-10 flex h-full min-h-0 flex-col pb-[env(safe-area-inset-bottom)]">
        <h2 id={titleId} className="sr-only">Original email</h2>
        <div className="flex shrink-0 justify-end gap-2 px-4 pt-[max(1rem,env(safe-area-inset-top))] pb-3">
          {downloadUrl ? <a href={downloadUrl} download={download.name} aria-label="Download original email" className={control}>
            <Download className="w-5 h-5" />
          </a> : <button type="button" disabled aria-label={downloadError ? 'Download unavailable' : 'Preparing download'} className={`${control} opacity-40`}>
            <Download className="w-5 h-5" />
          </button>}
          <button type="button" autoFocus onClick={onClose} aria-label="Close email" className={control}><X className="w-5 h-5" /></button>
        </div>
        {downloadError && <p role="status" className="px-4 pb-3 text-sm text-app-sub">Download unavailable. Close and reopen to try again.</p>}
        {error ? <p role="alert" className="p-4 text-sm text-rose-600">Couldn’t open the email. Close and reopen to try again.</p>
          : html ? <iframe title="Formatted booking email" sandbox="" referrerPolicy="no-referrer" srcDoc={html}
            className="w-full flex-1 min-h-0 border-0 bg-transparent" />
          : <p role="status" className="p-4 text-sm text-app-sub">Loading email…</p>}
      </div>
    </dialog>, document.getElementById('import-modal-root') ?? document.body,
  );
}

/**
 * The original confirmation(s) a booking was imported from — the e-ticket PDF,
 * the screenshot, the email. Links are resolved on open rather than on tap:
 * iOS blocks a window opened after an await.
 */
export function BookingDocuments({ docs, notes, label = 'Booking documents' }: { docs?: BookingDocument[]; notes?: string; label?: string }) {
  const [urls, setUrls] = useState<Record<string, string>>({});
  const [unavailable, setUnavailable] = useState<Record<string, boolean>>({});
  const [preview, setPreview] = useState<ReturnType<typeof bookingDocumentEntries>[number] | null>(null);
  const entries = bookingDocumentEntries(docs || []);
  const key = (docs || []).map(d => d.path).join('|');
  useEffect(() => {
    let live = true;
    (docs || []).forEach(d => {
      getDownloadURL(ref(storage, d.path))
        .then(url => { if (live) setUrls(u => ({ ...u, [d.path]: url })); })
        .catch(() => { if (live) setUnavailable(u => ({ ...u, [d.path]: true })); });
    });
    return () => { live = false; };
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!docs?.length && !notes?.trim()) return null;
  return (
    <div className="space-y-1.5">
      {!!docs?.length && <div className="ds-label">{label}</div>}
      {entries.map(entry => { const d = entry.document; return d.kind === 'email-html' || d.contentType === 'message/rfc822' || d.name === 'email.txt' ? (
        <button key={d.path} type="button" onClick={() => setPreview(entry)}
          className="flex w-full items-center gap-2.5 p-3 rounded-xl bg-app-faint border border-app-border/30 text-sm text-app-text hover:border-gold/40">
          <FileText className="w-4 h-4 text-gold shrink-0" />
          <span className="truncate flex-1 text-left">{d.kind === 'email-html' || d.name === 'original.eml' || d.name === 'email.txt' ? 'Original email' : d.name}</span>
          <span className="text-xs text-app-sub shrink-0">Open</span>
        </button>
      ) : (
        <a
          key={d.path}
          href={urls[d.path]}
          target="_blank"
          rel="noopener noreferrer"
          aria-disabled={!urls[d.path]}
          className={`flex items-center gap-2.5 p-3 rounded-xl bg-app-faint border border-app-border/30 text-sm text-app-text ${urls[d.path] ? 'hover:border-gold/40' : 'opacity-50 pointer-events-none'}`}
        >
          <FileText className="w-4 h-4 text-gold shrink-0" />
          <span className="truncate flex-1">{d.name}</span>
          <span className="text-xs text-app-sub shrink-0" aria-live="polite">
            {urls[d.path] ? 'Open' : unavailable[d.path] ? "Couldn't load" : 'Loading…'}
          </span>
        </a>
      ); })}
      {preview && <BookingEmailViewer key={`${preview.document.path}:${preview.download.path}`} {...preview} onClose={() => setPreview(null)} />}
      {!!notes?.trim() && <div className="pt-3 space-y-1.5"><div className="ds-label">Booking notes</div>
        <p className="text-sm text-app-sub whitespace-pre-wrap break-words">{notes}</p></div>}
    </div>
  );
}
